import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router";
import { Component, lazy, Suspense, type ReactNode } from "react";
import { LoginPage } from "../features/account/LoginPage";
import { SessionProvider, useSession } from "./session";
import styles from "./App.module.css";

const LibraryPage=lazy(()=>import("../features/library/LibraryPage").then(m=>({default:m.LibraryPage})));
const WritingPage=lazy(()=>import("../features/writing/WritingPage").then(m=>({default:m.WritingPage})));
const WorldPage=lazy(()=>import("../features/world/WorldPage").then(m=>({default:m.WorldPage})));
const VerifyEmailPage=lazy(()=>import("../features/account/VerifyEmailPage").then(m=>({default:m.VerifyEmailPage})));
const AdministrationPage=lazy(()=>import("../features/account/AdministrationPage").then(m=>({default:m.AdministrationPage})));
const PasswordRecoveryPage=lazy(()=>import("../features/account/PasswordRecoveryPage").then(m=>({default:m.PasswordRecoveryPage})));
const PublishingPage=lazy(()=>import("../features/writing/PublishingPage").then(m=>({default:m.PublishingPage})));
const WorkManagementPage=lazy(()=>import("../features/writing/WorkManagementPage").then(m=>({default:m.WorkManagementPage})));
const WorkSettingsPage=lazy(()=>import("../features/writing/WorkSettingsPage").then(m=>({default:m.WorkSettingsPage})));
const UsagePage=lazy(()=>import("../features/account/UsagePage").then(m=>({default:m.UsagePage})));

class PageBoundary extends Component<{children:ReactNode},{failed:boolean}> {
  override state={failed:false};
  static getDerivedStateFromError(){return {failed:true};}
  override render(){return this.state.failed?<div className={styles.loading} role="alert"><p>页面暂时无法打开，请检查网络后重新加载。</p><button type="button" onClick={()=>window.location.reload()}>重新加载</button></div>:this.props.children;}
}

function PageContent({children}:{children:ReactNode}){
  const location=useLocation();
  return <PageBoundary key={location.pathname}><Suspense fallback={<p className={styles.loading} role="status">正在打开页面…</p>}>{children}</Suspense></PageBoundary>;
}

function RequireSession({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useSession();
  if (isLoading) return <p className={styles.loading}>正在检查会话…</p>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * 页面按路由加载，登录和书库不用提前解析正文编辑器。
 * Workbox 仍预缓存全部构建资源，已缓存页面可离线导航。
 */
export function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <PageContent>
        <Routes>
          <Route path="/works/:workId/manage" element={<RequireSession><WorkManagementPage/></RequireSession>}/>
          <Route path="/works/:workId/settings" element={<RequireSession><WorkSettingsPage/></RequireSession>}/>
          <Route path="/works/:workId/publishing" element={<RequireSession><PublishingPage/></RequireSession>}/>
          <Route path="/works/:workId/world" element={<RequireSession><WorldPage/></RequireSession>}/>
          <Route path="/admin" element={<RequireSession><AdministrationPage/></RequireSession>}/>
          <Route path="/usage" element={<RequireSession><UsagePage/></RequireSession>}/>
          <Route path="/forgot-password" element={<PasswordRecoveryPage/>}/>
          <Route path="/reset-password" element={<PasswordRecoveryPage/>}/>
          <Route path="/login" element={<LoginPage />} />
          {/* 邮件里的验证链接：必须在会话守卫之外，未登录也要能打开。 */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route
            path="/works/:workId"
            element={
              <RequireSession>
                <WritingPage />
              </RequireSession>
            }
          />
          <Route
            path="*"
            element={
              <RequireSession>
                <LibraryPage />
              </RequireSession>
            }
          />
        </Routes>
        </PageContent>
      </SessionProvider>
    </BrowserRouter>
  );
}
